/**
 * @file product-list.ts
 *
 * Shared constants and types for the Products list/grid screen: view/sort types,
 * the contextual-menu action set, and the stock-status label and indicator maps.
 * Kept separate from the components so they stay a single source of truth. (The
 * pager logic lives in the shared `components/build-page-items.ts`.)
 */

import type { IconName } from '@group.one/gravity';
import type { MessageDescriptor } from 'react-intl';
import { adminMessages } from '../i18n/messages';
import { WooProductType, type ShopProduct } from '../api/shop-types';

export type ViewMode = 'grid' | 'list';
/** Sortable list columns. `status`/`stock` map to the admin-gated `/v1` sort keys. */
export type SortColumn = 'title' | 'price' | 'status' | 'stock';
export type SortOrder = 'asc' | 'desc';
export type ProductMenuAction = 'view' | 'edit' | 'duplicate' | 'delete';

/** Geometry for anchoring the desktop contextual menu near its trigger. */
export const CONTEXT_MENU_WIDTH = 300;
export const CONTEXT_MENU_HEIGHT = 122;
export const CONTEXT_MENU_VIEWPORT_MARGIN = 8;

/** Stock-status keys offered in the stock filter, in display order. */
// The stock FILTER offers only the two states commerce-engine actually derives (in / out of
// stock). `onbackorder` is intentionally excluded: CE never emits it (stockStatusOf /
// stockStatusFromSummary yield only instock/partialstock/outofstock), so offering it as a filter
// would map to no `availability` param and silently return the whole catalog. Each remaining key
// maps 1:1 to a server-side `availability` value, so a selection always filters. (STOCK_LABELS /
// STOCK_STATE keep an `onbackorder` entry for any legacy display lookup — harmless, never selected.)
export const STOCK_KEYS = ['instock', 'outofstock'] as const;

/** Localized label per stock-status key. `partialstock` = some (not all) variants out. */
export const STOCK_LABELS: Record<string, MessageDescriptor> = {
  instock: adminMessages.productsStockInstock,
  outofstock: adminMessages.productsStockOutofstock,
  onbackorder: adminMessages.productsStockOnbackorder,
  partialstock: adminMessages.productsStockPartial,
};

/** Gravity indicator class per stock status. `partialstock` renders amber (attention). */
export const STOCK_INDICATOR: Record<string, string> = {
  instock: 'gv-state-positive',
  outofstock: 'gv-state-critical',
  onbackorder: 'gv-state-attention',
  partialstock: 'gv-state-attention',
};

/** Gravity indicator class per publication status. */
export const STATUS_INDICATOR: Record<string, string> = {
  publish: 'gv-state-positive',
  draft: 'gv-state-attention',
  private: 'gv-state-informative',
};

/** Publication-status keys offered in the server-side status filter, in display order. */
export const STATUS_FILTER_KEYS = ['publish', 'draft', 'private'] as const;

/** Localized label per publication-status filter key (Visible / Draft / Hidden). */
export const STATUS_FILTER_LABELS: Record<string, MessageDescriptor> = {
  publish: adminMessages.productsStatusPublished,
  draft: adminMessages.productsStatusDraft,
  private: adminMessages.productsStatusHidden,
};

/**
 * The type-badge descriptor for a product: `Digital` for a downloadable product, `With {x}
 * variants` for a variable product (from `variant_count`), else `Simple`. Predicate matches
 * the mappers (`type === Variable` from `optionCount`/`options.length`).
 *
 * @param product - The product to badge.
 *
 * @returns The message descriptor and its interpolation values.
 */
export function productTypeBadge(product: ShopProduct): {
  message: MessageDescriptor;
  values?: Record<string, number>;
} {
  if (product.virtual) {
    return { message: adminMessages.productsTypeDigital };
  }
  if (product.type === WooProductType.Variable) {
    return {
      message: adminMessages.productsTypeVariants,
      values: { count: product.variant_count ?? 0 },
    };
  }
  return { message: adminMessages.productsTypeSimple };
}

/** A product's price line, plus the struck-through regular price beside a sale. */
export interface ProductPriceDisplay {
  /** The formatted price: a range for divergent variants, else the effective price. `null` when unpriced. */
  label: string | null;
  /** The struck-through regular price beside a single sale price; `null` for ranges and unsold products. */
  regular: string | null;
}

/**
 * The price a catalog surface shows for a product — the same answer for the list row, the
 * grid card, and the detail drawer (mirrors WSB's `productPriceDisplay`).
 *
 * The summary `price` is one REPRESENTATIVE variant (the oldest enabled one, not the
 * cheapest), so on a product whose variants carry their own prices a single number would be
 * whichever variant happened to be created first. The mapped `price_range` is the honest
 * answer there: a spread renders as `min – max` with no sale strikethrough (the range spans
 * regular prices). Products whose variants all share one price keep the familiar single,
 * sale-aware figure.
 *
 * @param product - The product to price.
 * @param currencySymbol - The shop's currency symbol prefix.
 *
 * @returns The price line and the optional struck-through regular price.
 */
export function productPriceDisplay(
  product: ShopProduct,
  currencySymbol: string,
): ProductPriceDisplay {
  const range = product.price_range;
  if (product.type === WooProductType.Variable && range && range.min !== range.max) {
    return {
      label: `${currencySymbol}${range.min} – ${currencySymbol}${range.max}`,
      regular: null,
    };
  }
  const effective = product.sale_price || product.price;
  return {
    label: effective ? `${currencySymbol}${effective}` : null,
    regular:
      product.regular_price && product.regular_price !== effective
        ? `${currencySymbol}${product.regular_price}`
        : null,
  };
}

/** Contextual-menu actions for a product row/card. */
export const PRODUCT_MENU_ACTIONS: {
  value: ProductMenuAction;
  iconName: IconName;
  label: MessageDescriptor;
}[] = [
  { value: 'view', iconName: 'visibility', label: adminMessages.productsMenuView },
  { value: 'edit', iconName: 'edit', label: adminMessages.productsMenuEdit },
  {
    value: 'duplicate',
    iconName: 'content_copy',
    label: adminMessages.productsMenuDuplicate,
  },
  { value: 'delete', iconName: 'delete', label: adminMessages.productsMenuDelete },
];
